import { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { updateLesson, deleteLesson } from '../../actions/certificationActions'
import { vimeoEmbedUrl, showGenericError } from '../../utility/certificationBuilder'
import { useForm } from './useForm'
import EditorHeader from './EditorHeader'
import BuilderField from './BuilderField'
import VimeoEmbed from './VimeoEmbed'
import DeleteConfirmationModal from './DeleteConfirmationModal'
import Input from '../common/form/Input'
import Toggle from '../common/form/Toggle'

import classes from './LessonEditor.module.scss'

const lessonForm = (lesson) => ({ title: lesson.title, videoUrl: lesson.videoUrl || '', body: lesson.body || '' })

const LessonEditor = ({ lesson, sectionNumber, lessonNumber, onDeleted }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { form, setForm, errors, setErrors, setField } = useForm(() => lessonForm(lesson))
  const [isSaving, setIsSaving] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const embedUrl = vimeoEmbedUrl(form.videoUrl)
  const dirty = JSON.stringify(form) !== JSON.stringify(lessonForm(lesson))

  const handleSave = (e) => {
    e.preventDefault()
    if (form.videoUrl.trim() !== '' && !embedUrl) { setErrors({ videoUrl: t('certificationBuilder.lesson.invalidVideo') }); return }

    setIsSaving(true)
    dispatch(updateLesson({ sectionId: lesson.sectionId, id: lesson.id, ...form })).unwrap()
      .then(({ lesson: saved }) => {
        setForm(lessonForm(saved))
        toast.success(t('certificationBuilder.lesson.saveSuccess'), { position: toast.POSITION.TOP_RIGHT })
      })
      .catch((apiErrors) => apiErrors?.videoUrl ? setErrors({ videoUrl: apiErrors.videoUrl[0] }) : showGenericError())
      .finally(() => setIsSaving(false))
  }

  const toggleDraft = () => {
    setIsSaving(true)
    dispatch(updateLesson({ sectionId: lesson.sectionId, id: lesson.id, draft: !lesson.draft })).unwrap()
      .catch(showGenericError)
      .finally(() => setIsSaving(false))
  }

  const handleDelete = () => {
    setIsDeleting(true)
    dispatch(deleteLesson({ sectionId: lesson.sectionId, id: lesson.id })).unwrap()
      .then(onDeleted)
      .catch(() => { showGenericError(); setIsDeleting(false) })
  }

  return (
    <form onSubmit={handleSave}>
      <EditorHeader
        eyebrow={t('certificationBuilder.lesson.eyebrow', { section: sectionNumber, lesson: lessonNumber })}
        action={<button type='button' className={classes.textButton} onClick={() => setShowDeleteModal(true)}>{t('common.delete')}</button>}
      />

      <div className={classNames('d-flex flex-column', classes.body)}>
        <BuilderField label={t('certificationBuilder.lesson.title')} htmlFor='lessonTitle' required>
          <Input
            id='lessonTitle'
            type='text'
            value={form.title}
            placeholder={t('certificationBuilder.lesson.titlePlaceholder')}
            onChange={(e) => setField('title', e.target.value)}
          />
        </BuilderField>

        <BuilderField label={t('certificationBuilder.lesson.video')} htmlFor='lessonVideoUrl' hint={t('certificationBuilder.lesson.videoHint')}>
          <Input
            id='lessonVideoUrl'
            type='text'
            value={form.videoUrl}
            placeholder={t('certificationBuilder.lesson.videoPlaceholder')}
            onChange={(e) => setField('videoUrl', e.target.value)}
            error={errors.videoUrl}
          />
          {embedUrl && <VimeoEmbed src={embedUrl} />}
        </BuilderField>

        <BuilderField label={t('certificationBuilder.lesson.content')} htmlFor='lessonBody'>
          <Input
            id='lessonBody'
            type='textarea'
            value={form.body}
            placeholder={t('certificationBuilder.lesson.contentPlaceholder')}
            onChange={(e) => setField('body', e.target.value)}
            classNames={{ inputContainer: embedUrl ? classes.notesArea : classes.articleArea }}
          />
        </BuilderField>

        <div className={classNames('d-flex align-items-center justify-content-between', classes.footer)}>
          <label className={classNames('d-flex align-items-center mb-0', classes.draftToggle)}>
            <Toggle size='small' checked={!lesson.draft} onChange={toggleDraft} disabled={isSaving} />
            {t(lesson.draft ? 'certificationBuilder.lesson.draftOn' : 'certificationBuilder.lesson.draftOff')}
          </label>
          <button type='submit' className={classNames('btn btn-primary text-white flex-shrink-0', classes.saveButton)} disabled={!dirty || isSaving}>
            {isSaving && <i className='fa fa-spinner fa-spin mr-2' aria-hidden='true' />}
            {t('certificationBuilder.lesson.save')}
          </button>
        </div>
      </div>

      <DeleteConfirmationModal
        show={showDeleteModal}
        title={t('certificationBuilder.lesson.deleteTitle')}
        message={t('certificationBuilder.lesson.deleteMessage', { title: lesson.title || t('certificationBuilder.curriculum.untitledLesson') })}
        isLoading={isDeleting}
        onClose={() => setShowDeleteModal(false)}
        onProceed={handleDelete}
      />
    </form>
  )
}

LessonEditor.propTypes = {
  lesson: PropTypes.shape({
    id: PropTypes.number.isRequired,
    sectionId: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    videoUrl: PropTypes.string,
    body: PropTypes.string,
    draft: PropTypes.bool.isRequired,
  }).isRequired,
  sectionNumber: PropTypes.number.isRequired,
  lessonNumber: PropTypes.number.isRequired,
  onDeleted: PropTypes.func.isRequired,
}

export default LessonEditor
